/**
 * Generate a ULID-like ID using crypto.randomUUID as base.
 * For true ULIDs in production, use the `ulid` package.
 * This is a lightweight alternative for edge environments.
 */
export function generateId(): string {
  return crypto.randomUUID().replace(/-/g, '')
}

/**
 * Create a URL-safe slug from a string.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Format a number as currency.
 */
export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * Format a number as percentage.
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Calculate percentage safely (avoid division by zero).
 */
export function percent(part: number, total: number): number {
  if (total === 0) return 0
  return (part / total) * 100
}

/**
 * Get today's date as YYYY-MM-DD in UTC.
 */
export function todayUTC(): string {
  return new Date().toISOString().split('T')[0]
}

/**
 * Check if a date is older than N days.
 */
export function isOlderThanDays(dateStr: string | null, days: number): boolean {
  if (!dateStr) return true
  const date = new Date(dateStr)
  const threshold = new Date()
  threshold.setDate(threshold.getDate() - days)
  return date < threshold
}

/**
 * Chunk an array into batches.
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Retry an async operation with exponential backoff.
 */
export async function retry<T>(
  fn: () => Promise<T>,
  opts: { maxRetries?: number; baseDelayMs?: number } = {},
): Promise<T> {
  const { maxRetries = 3, baseDelayMs = 1000 } = opts
  let lastError: unknown
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastError = err
      if (attempt < maxRetries) {
        const jitter = Math.random() * 500
        await sleep(baseDelayMs * Math.pow(2, attempt) + jitter)
      }
    }
  }
  throw lastError
}
